import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaretRightIcon, CheckIcon } from "@phosphor-icons/react";
import { AppButton, ConfirmationModal } from "@libs/zapi-ui/custom";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectGenerationState, TChapterGenState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";
import {
  DEEP_DIVE_SECTIONS,
  FULL_SUMMARY_SLUG,
  findExecSummaryChapter
} from "@/zapi-frontend/shared/lib/projectChapters";
import { AiMessage } from "./AiMessage";
import { SyncedSpinner } from "@/zapi-frontend/shared/ui/SyncedSpinner";

function StateIcon({ state }: { state: TChapterGenState }) {
  switch (state) {
    case "done":
      return <CheckIcon size={16} weight="bold" className="text-innoveraGreen-500 shrink-0" />;
    case "generating":
      return (
        <SyncedSpinner className="size-[14px] shrink-0 rounded-full border-[1.5px] border-[#ECECEE] border-t-[#18181B]" />
      );
    case "queued":
      return <div className="size-[14px] shrink-0 rounded-full border-[1.5px] border-[#ECECEE]" />;
  }
}

const stateTextColor: Record<TChapterGenState, string> = {
  done: "text-zinc-900",
  generating: "text-zinc-900",
  queued: "text-zinc-400"
};

function ChapterProgressRow({
  state,
  name,
  onOpen
}: {
  state: TChapterGenState;
  name: string;
  onOpen?: () => void;
}) {
  const { t } = useTranslation("projectHome");

  if (state === "done" && onOpen) {
    return (
      <button
        onClick={onOpen}
        aria-label={t("generation.openChapter", { name })}
        className="flex items-center gap-2 min-h-[22px] w-full -mx-1.5 px-1.5 py-0.5 rounded-md text-left cursor-pointer hover:bg-innoveraZinc-100 transition-colors"
      >
        <StateIcon state="done" />
        <span className="text-[14px] leading-[22px] text-zinc-900">{name}</span>
        <CaretRightIcon size={14} className="ml-auto shrink-0 text-innoveraZinc-500" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 min-h-[22px]">
      <StateIcon state={state} />
      <span className={`text-[14px] leading-[22px] ${stateTextColor[state]}`}>{name}</span>
    </div>
  );
}

type TProjectGenerationProgressProps = {
  projectId: string;
  onCancel?: () => void;
  isCancelling?: boolean;
};

export function ProjectGenerationProgress({ projectId, onCancel, isCancelling }: TProjectGenerationProgressProps) {
  const { t } = useTranslation("projectHome");
  const { isSetupActive, isSettingUp, isHomeGenerating, visibleChapters, getChapterState } =
    useProjectGenerationState(projectId);
  const { setActiveChapter } = useChapterStore();
  const [, setSearchParams] = useSearchParams();
  const { isLabs } = useMeStore();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (!isSetupActive || isSettingUp) return null;

  const execChapter = findExecSummaryChapter(visibleChapters);
  const memoChapter = visibleChapters.find((c) => c.projectTemplateChapter.slug === FULL_SUMMARY_SLUG);
  const specialIds = new Set([execChapter?.id, memoChapter?.id]);
  const rest = visibleChapters.filter((c) => !specialIds.has(c.id));
  const templateHasSections = rest.some((c) => c.projectTemplateChapter.section);
  const sectioned = DEEP_DIVE_SECTIONS.flatMap((section) =>
    rest.filter((c) => c.projectTemplateChapter.section === section.key)
  );
  const unsectioned = rest.filter(
    (c) => !c.projectTemplateChapter.section && (isLabs || !templateHasSections)
  );
  const orderedChapters = [execChapter, memoChapter, ...sectioned, ...unsectioned].filter(
    (c): c is NonNullable<typeof c> => !!c
  );

  const openChapter = (chapterId: number) => {
    setActiveChapter(chapterId.toString());
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", "document");
      params.set("chapterId", chapterId.toString());
      params.delete("chapterSubsection");
      params.delete("view");
      return params;
    });
  };

  return (
    <AiMessage className="w-full">
      <p className="text-[14px] leading-[22px] text-zinc-900">{t("generation.progressStarted")}</p>
      <p className="text-[14px] leading-[22px] text-zinc-900">{t("generation.progressOrder")}</p>
      <div className="flex flex-col gap-1.5 w-full">
        <ChapterProgressRow state={isHomeGenerating ? "generating" : "queued"} name={t("sidebar.home")} />
        {orderedChapters.map((chapter) => (
          <ChapterProgressRow
            key={chapter.id}
            state={getChapterState(chapter)}
            name={chapter.projectTemplateChapter.name}
            onOpen={() => openChapter(chapter.id)}
          />
        ))}
      </div>
      {onCancel && (
        <>
          <AppButton
            size="sm"
            appearance="outlined"
            color="zinc150"
            className="text-innoveraZinc-900 shadow-none"
            onClick={() => setIsConfirmOpen(true)}
            isLoading={isCancelling}
          >
            {t("generation.cancelAnalysis")}
          </AppButton>
          <ConfirmationModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={onCancel}
            title={t("generation.cancelAnalysis")}
            description={t("generation.cancelAnalysisDescription")}
            confirmText={t("generation.cancelAnalysis")}
            cancelText={t("generation.keepGenerating")}
          />
        </>
      )}
    </AiMessage>
  );
}
